import type { ColumnDef } from '@tanstack/react-table'
import {
  Copy,
  Crown,
  Eye,
  Mail,
  MoreHorizontal,
  RefreshCw,
  Trash2,
  Users,
} from 'lucide-react'
import { useState } from 'react'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { teamService } from '../services/teamService'
import type { PendingInvite } from '../types/team.types'

interface PendingInvitesTableProps {
  pendingInvites: PendingInvite[]
  loading?: boolean
  onInviteResent?: () => void
  onInviteRemoved?: () => void
}

export function PendingInvitesTable({
  pendingInvites,

  onInviteResent,
  onInviteRemoved,
}: PendingInvitesTableProps) {
  const [processingInvite, setProcessingInvite] = useState<string | null>(null)

  const handleResendInvite = async (invite: PendingInvite) => {
    setProcessingInvite(invite.id)
    try {
      await teamService.resendInvite(invite.id)
      onInviteResent?.()
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setProcessingInvite(null)
    }
  }

  const handleCopyInviteLink = async (inviteLink: string) => {
    try {
      await navigator.clipboard.writeText(inviteLink)
      // Invite link copied to clipboard
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  const handleRemoveInvite = async (invite: PendingInvite) => {
    setProcessingInvite(invite.id)
    try {
      await teamService.removeTeamMember(invite.id)
      onInviteRemoved?.()
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setProcessingInvite(null)
    }
  }

  const getRoleIcon = (role: PendingInvite['role']) => {
    switch (role) {
      case 'admin':
        return <Crown className='w-4 h-4 text-amber-500' />
      case 'member':
        return <Users className='w-4 h-4 text-blue-500' />
      case 'viewer':
        return <Eye className='w-4 h-4 text-slate-500' />
    }
  }

  const getRoleVariant = (role: PendingInvite['role']) => {
    switch (role) {
      case 'admin':
        return 'default' as const
      case 'member':
        return 'secondary' as const
      case 'viewer':
        return 'outline' as const
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    )

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
    return `${Math.floor(diffDays / 30)} months ago`
  }

  const columns: ColumnDef<PendingInvite>[] = [
    {
      accessorKey: 'email',
      header: 'Invitee',
      cell: ({ row }) => {
        const invite = row.original
        return (
          <div className='flex items-center gap-3'>
            <div className='w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-pink-600 flex items-center justify-center text-white text-sm font-medium'>
              {(invite.name || invite.email).charAt(0).toUpperCase()}
            </div>
            <div className='min-w-0'>
              <div className='font-medium truncate'>
                {invite.name || invite.email.split('@')[0]}
              </div>
              <div className='text-sm text-muted-foreground truncate'>
                {invite.email}
              </div>
            </div>
          </div>
        )
      },
      size: 300,
    },
    {
      accessorKey: 'role',
      header: 'Role',
      cell: ({ row }) => {
        const invite = row.original
        return (
          <div className='flex items-center gap-2'>
            {getRoleIcon(invite.role)}
            <Badge variant={getRoleVariant(invite.role)}>
              {invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}
            </Badge>
          </div>
        )
      },
      size: 120,
    },
    {
      accessorKey: 'created_by_name',
      header: 'Created By',
      cell: ({ row }) => {
        const invite = row.original
        return (
          <div className='text-sm'>
            <div className='font-medium'>{invite.created_by_name}</div>
          </div>
        )
      },
      size: 150,
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }) => {
        const invite = row.original
        return (
          <div className='text-sm'>
            <div>{formatDate(invite.created_at)}</div>
            <div className='text-xs text-muted-foreground'>
              {getTimeAgo(invite.created_at)}
            </div>
          </div>
        )
      },
      size: 150,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const invite = row.original
        const isProcessing = processingInvite === invite.id

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                className='h-8 w-8 p-0'
                disabled={isProcessing}
              >
                <span className='sr-only'>Open menu</span>
                {isProcessing ? (
                  <RefreshCw className='h-4 w-4 animate-spin' />
                ) : (
                  <MoreHorizontal className='h-4 w-4' />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                onClick={() => handleCopyInviteLink(invite.invite_link)}
              >
                <Copy className='mr-2 h-4 w-4' />
                Copy Invite Link
              </DropdownMenuItem>

              <DropdownMenuItem onClick={() => handleResendInvite(invite)}>
                <Mail className='mr-2 h-4 w-4' />
                Resend Invite
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleRemoveInvite(invite)}
                className='text-red-600'
              >
                <Trash2 className='mr-2 h-4 w-4' />
                Remove Invite
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      size: 60,
    },
  ]

  return (
    <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
      <EnhancedTable
        key='pending-invites-table'
        data={pendingInvites}
        columns={columns}
        height='calc(100vh - 400px)'
        className='bg-transparent border-0'
      />
    </div>
  )
}
